import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import MangaCard from "@/components/feature/MangaCard";
import { categories, mangaWorks } from "@/mocks/manga";

type SortKey = "综合" | "评分" | "更新";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const searchQ = params.get("q") || "";

  const category = categories.find((c) => c.slug === slug);
  const [sort, setSort] = useState<SortKey>("综合");
  const [status, setStatus] = useState<"全部" | "连载中" | "已完结">("全部");

  const list = useMemo(() => {
    let base = category
      ? mangaWorks.filter((w) => w.categorySlug === category.slug)
      : mangaWorks;
    if (searchQ) {
      const q = searchQ.toLowerCase();
      base = base.filter(
        (w) =>
          w.title.toLowerCase().includes(q) ||
          w.intro.toLowerCase().includes(q) ||
          w.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    if (status !== "全部") base = base.filter((w) => w.status === status);
    if (sort === "评分") base = [...base].sort((a, b) => b.rating - a.rating);
    if (sort === "更新") base = [...base].sort((a, b) => b.episodes - a.episodes);
    return base;
  }, [category, searchQ, sort, status]);

  const title = searchQ
    ? `搜索「${searchQ}」- 吹雪漫画网`
    : category
    ? `${category.name}在线阅读 - 吹雪漫画网`
    : "全部作品 - 吹雪漫画网";
  const description = searchQ
    ? `吹雪漫画网「${searchQ}」搜索结果：雪景、冬日、雪国、冰雪、冬物语、雪女等冬日题材漫画在线阅读。`
    : category
    ? `${category.name}专区收录${category.description}高清在线看，分类清晰，点开即读。`
    : "吹雪漫画网全站作品：涵盖雪景漫画、冬日漫画、雪国漫画、冬物语、冰雪漫画、雪女漫画等冬日题材，高清在线看。";

  return (
    <Layout>
      <Seo
        title={title}
        description={description}
        keywords="吹雪漫画网,雪景漫画,冬日漫画,雪国漫画,冬物语,冰雪漫画,雪女漫画"
        canonical={`https://secretom.github.io/category/${slug}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: title,
            description,
            url: `https://secretom.github.io/category/${slug}`,
            mainEntity: {
              "@type": "ItemList",
              itemListElement: list.map((w, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: w.title,
                url: `https://secretom.github.io/work/${w.id}`,
              })),
            },
          },
        ]}
      />

      {/* 分类头图 */}
      <section className="relative flex min-h-[300px] w-full items-end overflow-hidden pt-24 md:min-h-[360px]">
        <div className="absolute inset-0 bg-background-100"></div>
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary-500/10 blur-3xl"></div>
        <div className="absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-accent-500/10 blur-3xl"></div>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-10 md:px-6 md:pb-14">
          <nav aria-label="面包屑" className="mb-5 flex items-center gap-1.5 text-sm text-foreground-600">
            <Link to="/" className="hover:text-primary-500">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-400">
              {searchQ ? "搜索结果" : category ? category.name : "全部作品"}
            </span>
          </nav>
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary-500 text-2xl text-background-950 md:h-16 md:w-16">
              <i className={category ? category.icon : "ri-grid-line"}></i>
            </span>
            <div>
              <h1 className="font-heading text-3xl font-black text-foreground-950 md:text-4xl">
                {searchQ ? `「${searchQ}」的搜索结果` : category ? category.name : "全部作品"}
              </h1>
              <p className="mt-2 text-sm text-foreground-500 md:text-base">
                {searchQ
                  ? `为你找到 ${list.length} 部与「${searchQ}」相关的冬日漫画`
                  : category
                  ? `${category.description} 共收录 ${list.length} 部佳作`
                  : "吹雪漫画网全站冬日题材作品一览"}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 筛选 + 列表 */}
      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {(["综合", "评分", "更新"] as SortKey[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSort(s)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  sort === s ? "bg-primary-500 text-background-950" : "bg-background-100 text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {(["全部", "连载中", "已完结"] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  status === s ? "bg-secondary-500 text-background-950" : "bg-background-100 text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {list.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {list.map((w) => (
              <MangaCard key={w.id} work={w} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-background-200/60 bg-background-100 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-3xl text-foreground-500">
              <i className="ri-snowy-line"></i>
            </span>
            <p className="mt-4 text-sm text-foreground-600">没有找到相关作品</p>
            <Link to="/" className="mt-4 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-950 hover:bg-primary-600">
              返回首页
            </Link>
          </div>
        )}
      </section>
    </Layout>
  );
}